import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { updateOrgConfig } from '../../api/org-config'
import { Banner } from '../../components/primitives/Banner'
import { Button } from '../../components/primitives/Button'
import { Field } from '../../components/primitives/Field'
import { applyTheme, THEME_KEYS, THEME_TOKEN_READ_VAR } from '../../lib/apply-theme'
import { meetsAA } from '../../lib/contrast'
import { useOrgConfig } from '../../lib/org-config-context'

const HEX_RE = /^#[0-9a-fA-F]{6}$/

function currentValue(theme: Record<string, string>, key: string): string {
  const fromTheme = theme[key]
  if (fromTheme && HEX_RE.test(fromTheme)) return fromTheme
  const cssVar = THEME_TOKEN_READ_VAR[key]
  if (cssVar && typeof window !== 'undefined') {
    const computed = getComputedStyle(document.documentElement)
      .getPropertyValue(cssVar)
      .trim()
    if (HEX_RE.test(computed)) return computed
  }
  return '#000000'
}

/** On-screen color-theme editor. Live-previews via applyTheme as you edit,
 *  blocks save on a failing ink/surface contrast pair (WCAG AA), and PATCHes
 *  org_config.theme on save. */
export function AdminThemeTab(): JSX.Element {
  const org = useOrgConfig()
  const queryClient = useQueryClient()

  const initial = useMemo(() => {
    const seed: Record<string, string> = {}
    for (const key of THEME_KEYS) seed[key] = currentValue(org.theme, key)
    return seed
  }, [org.theme])

  const [draft, setDraft] = useState<Record<string, string>>(initial)
  const [saved, setSaved] = useState(false)

  const inkOnSurfaceOk = meetsAA(draft.ink ?? '#000000', draft.surface ?? '#FFFFFF')

  const mutation = useMutation({
    mutationFn: () => updateOrgConfig({ theme: draft }),
    onSuccess: () => {
      setSaved(true)
      void queryClient.invalidateQueries({ queryKey: ['org-config', 'public'] })
    },
  })

  function setToken(key: string, value: string): void {
    const next = { ...draft, [key]: value }
    setDraft(next)
    setSaved(false)
    // Live preview — only push valid hex values to the DOM.
    if (HEX_RE.test(value)) applyTheme({ [key]: value })
  }

  function reset(): void {
    setDraft(initial)
    applyTheme(org.theme) // back to the persisted theme
    setSaved(false)
  }

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 560 }}>
      {!inkOnSurfaceOk ? (
        <Banner
          kind="escalation"
          title="Low contrast"
          subtitle="Ink on Surface is below WCAG AA (4.5:1). Adjust before saving — text would be hard to read."
        />
      ) : null}

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {THEME_KEYS.map((key) => (
          <Field key={key} label={key[0]!.toUpperCase() + key.slice(1)}>
            <input
              type="color"
              aria-label={key}
              value={draft[key] ?? '#000000'}
              onChange={(e) => setToken(key, e.target.value)}
              style={{ width: '100%', height: 40, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}
            />
          </Field>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button
          variant="primary"
          disabled={!inkOnSurfaceOk || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Saving…' : 'Save theme'}
        </Button>
        <Button variant="soft" onClick={reset} disabled={mutation.isPending}>
          Reset
        </Button>
        {saved ? <span style={{ color: 'var(--success-fg)', fontSize: 13 }}>Saved</span> : null}
        {mutation.isError ? (
          <span style={{ color: 'var(--danger-fg)', fontSize: 13 }}>
            {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
          </span>
        ) : null}
      </div>
    </div>
  )
}
